import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * Fixed slots for a record page header (design audit S7). PageHeader renders
 * the title side, ModuleLayout renders the actions side, so project and
 * client records read the same: crumb, avatar, name, status, star on the
 * left; follow, one primary "Add" menu and the overflow on the right. The
 * primary does not change between tabs.
 */
export interface DetailHeaderSlots {
  /** Record name; the only content of the h1. */
  name: string
  /** Inline after the name, outside the h1: template badge, record switcher. */
  nameAdornment?: ReactNode
  /** Muted 12 px line under the name: linked client, code. */
  subtitle?: ReactNode
  /** Status cluster: state badge, health chip, lifecycle field. */
  status?: ReactNode
  /** Favorite toggle. */
  star?: ReactNode
  /** Follow or watch control; omit for records without one. */
  follow?: ReactNode
  /** The single "Add" menu, present on every tab. */
  primary?: ReactNode
  /** The "..." menu. */
  overflow?: ReactNode
}

export function DetailHeaderTitle({
  slots,
  avatar,
  titleClassName,
}: {
  slots: DetailHeaderSlots
  avatar?: ReactNode
  titleClassName: string
}) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      {avatar}
      <div className="flex min-w-0 flex-col">
        {/* The name sets the row height: the 36 px star/switcher buttons are
            pulled in so the subtitle sits right under the name. The larger
            row gap keeps wrapped controls from overlapping. On phones the
            row doesn't wrap: the name (clamped to two lines) shrinks so the
            switcher and star stay beside it instead of dropping to a line of
            their own. */}
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-2 max-sm:flex-nowrap [&>:not(h1)]:-my-1 [&>:not(h1)]:shrink-0">
          <h1 className={cn('min-w-0 leading-tight', titleClassName)}>
            {slots.name}
          </h1>
          {slots.nameAdornment}
          {slots.status}
          {slots.star}
        </div>
        {slots.subtitle && (
          <div className="text-xs leading-tight text-muted-foreground">
            {slots.subtitle}
          </div>
        )}
      </div>
    </div>
  )
}

export function DetailHeaderActions({ slots }: { slots: DetailHeaderSlots }) {
  if (!slots.follow && !slots.primary && !slots.overflow) return null
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      {slots.follow}
      {slots.primary}
      {slots.overflow}
    </div>
  )
}
